import { Search as SearchIcon } from '@mui/icons-material';
import { InputAdornment, Skeleton, Stack, TextField } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import { trim } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import Header from '/@/components/bayer/header';
import PageContainer from '/@/components/bayer/page-container';
import Drawing from '/@/components/drawing';
import useUnitProvider from '/@/core/context/unit';
import { useCatalogProductsByBrands } from '/@/hooks/catalog';
import useDebounce from '/@/hooks/debounce/useDebounce';
import { _ } from '/@/locale';
import { getCatalogBrands } from '/@/store/catalog/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { reverse } from '/@/utils/router';
import type { PositioningBucket } from '../../dashboard/rv/components/product-catalog';
import ProductExplorationListSidebar, { useFilters } from '../components/list-sidebar';
import ProductCard, { parseProductForProductCard } from '../components/product-card';
import ProductComparator from '../components/product-comparator';
import { useProductComparator } from '../hooks/product-comparator';
import { PRODUCT_EXPLORATION_TOUR_IDS } from '../tour/product-exploration-tour-ids';

// Route slug used by generic entry points (landing CTA, search suggestions, guided tour) that
// need *a* value for the required `:slug` route param but aren't targeting one specific brand -
// unlike a real brand slug (e.g. "preceon"), this never matches a brand, so the list below falls
// back to showing every published brand mixed together.
export const ALL_BRANDS_SLUG = 'all';

const ProductExplorationList = () => {
    const { type, slug } = useParams();
    const navigate = useNavigate();

    // Filters
    const [search, setSearch] = useState<string>();
    const debouncedSearch = useDebounce(search, 250);
    const { filters, setFilters, toggleFilterItem } = useFilters();
    const [searchParams] = useSearchParams();
    useEffect(() => {
        const crop = searchParams.get('crop');
        const type = searchParams.get('type');
        const technology = searchParams.get('technology');
        const zone = searchParams.get('zone');
        const cycle = searchParams.get('cycle');
        if (crop) toggleFilterItem({ filter: 'crops', value: Number(crop) });
        if (type) toggleFilterItem({ filter: 'types', value: Number(type) });
        if (technology) toggleFilterItem({ filter: 'technologies', value: Number(technology) });
        if (zone) toggleFilterItem({ filter: 'zones', value: Number(zone) });
        if (cycle) toggleFilterItem({ filter: 'cycles', value: cycle });
    }, [searchParams]);

    // Comparator
    const {
        selectProduct,
        isSelected,
        selectedProducts,
        selectedType,
        resetAndSelectMultipleProducts,
        resetSelectedProducts,
    } = useProductComparator();

    // Core
    const units = useUnitProvider();
    const country = useSelector(getSessionCountry);
    const catalogBrands = useSelector(getCatalogBrands);
    const cropBrands = useMemo(
        () => (catalogBrands || []).filter((b) => b.type === (type || 'corn')),
        [catalogBrands, type]
    );
    // A real brand slug in the URL (e.g. "preceon") narrows the fetch to just that brand;
    // ALL_BRANDS_SLUG (or any slug that doesn't match a published brand) fetches everyone.
    const activeBrand = cropBrands.find((b) => b.slug === slug);
    const brandsToFetch = activeBrand ? [activeBrand] : cropBrands;

    const products = useCatalogProductsByBrands(
        brandsToFetch,
        {
            crop: type || 'corn',
            country: country?.code || 'AR',
            page: 1,
            pageSize: 10000,
        },
        { enabled: cropBrands.length > 0 }
    );

    const parsedProducts = useMemo(() => {
        if (['corn', 'soy'].includes(type)) {
            return parseProductForProductCard(
                (products.data?.results || [])
                    .filter((p) => Boolean(_(p.catalogZone)))
                    .map((p) => ({ ...p, catalogType: type })),
                units
            );
        }
        return parseProductForProductCard(
            (products.data?.results || []).map((p) => ({ ...p, catalogType: type })),
            units
        );
    }, [products, type]);

    const filteredProducts = useMemo(
        () =>
            parsedProducts.filter((p) => {
                const trimmedSearch = trim(debouncedSearch);
                const filterBySearch =
                    p.name.toLowerCase().includes(trimmedSearch?.toLowerCase() || '') ||
                    p.chips.some((chip) =>
                        (chip?.props?.label?.toLowerCase() || '').includes(trimmedSearch?.toLowerCase() || '')
                    );

                let filterByZone = true;
                if ((filters?.zones?.size || 0) > 0) {
                    filterByZone = filters?.zones?.has(p?.catalogZoneId) || false;
                }

                let filterByCycle = true;
                if ((filters?.cycles?.size || 0) > 0) {
                    filterByCycle = filters?.cycles?.has(p?.cycle) || false;
                }

                let filterByTechnology = true;
                if ((filters?.technologies?.size || 0) > 0) {
                    filterByTechnology = (p.technologies || []).some((t) => filters?.technologies?.has(t.id));
                }

                let filterByType = true;
                if ((filters?.types?.size || 0) > 0) {
                    filterByType = filters?.types?.has(p?.type?.id) || false;
                }

                let filterByCrop = true;
                if ((filters?.crops?.size || 0) > 0) {
                    filterByCrop = p?.crops?.some((productCrop) => filters?.crops?.has(productCrop.id)) || false;
                }

                let filterByPositioning = true;
                if ((filters?.positioning?.size || 0) > 0) {
                    // Match if the product covers ANY of the selected canonical buckets.
                    filterByPositioning =
                        (p?.positioningBuckets || []).some((bucket: PositioningBucket) =>
                            filters?.positioning?.has(bucket.label)
                        ) || false;
                }

                return (
                    filterBySearch &&
                    filterByZone &&
                    filterByCycle &&
                    filterByTechnology &&
                    filterByType &&
                    filterByCrop &&
                    filterByPositioning
                );
            }),
        [debouncedSearch, parsedProducts, filters]
    );

    const tourListAnchorProductId = parsedProducts[0]?.id;

    useEffect(() => {
        mpTrackEvent('Product Exploration List', { crop: type as 'corn' | 'soy' | 'crop_protection' });
    }, [type]);

    return (
        <PageContainer
            header={
                <Header
                    title={_('Explora todos los productos')}
                    subtitle={_('Listado completo de híbridos y productos de protección de cultivos disponibles.')}
                    breadcrumbs={[
                        { label: _('Productos'), onClick: () => navigate(reverse('productExploration.main')) },
                        { label: type === 'crop_protection' ? _('Protección de cultivos') : _('Híbridos') },
                    ]}
                />
            }
        >
            <Stack spacing={5} mt={4}>
                <TextField
                    placeholder={_('Busca por nombre o tipo de producto')}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />

                <Grid2 container spacing={{ xs: 1, sm: 3 }}>
                    <Grid2 xs={6} sm={4} md={3}>
                        <ProductExplorationListSidebar
                            products={parsedProducts}
                            type={type}
                            slug={slug}
                            toggleFilterItem={toggleFilterItem}
                            filters={filters}
                            setFilters={setFilters}
                            totalProducts={filteredProducts.length}
                            setSearch={setSearch}
                        />
                    </Grid2>
                    <Grid2
                        xs
                        container
                        spacing={3}
                        sx={{
                            height: 'fit-content',
                        }}
                    >
                        {parsedProducts.length ? (
                            filteredProducts.length ? (
                                filteredProducts?.map(
                                    (
                                        {
                                            imageSrc,
                                            name,
                                            description,
                                            id,
                                            chips,
                                            technologies,
                                            typeLabel,
                                            catalogType,
                                            slugLabel,
                                            newProduct,
                                        },
                                        i
                                    ) => {
                                        return (
                                            <Grid2 key={id} xs={12} sm={6} lg={4}>
                                                <ProductCard
                                                    id={id}
                                                    imageSrc={imageSrc}
                                                    name={name}
                                                    description={description}
                                                    typeLabel={typeLabel}
                                                    chips={chips}
                                                    technologies={technologies}
                                                    selectProduct={() => {
                                                        selectProduct({ id });
                                                    }}
                                                    isSelected={isSelected({ id })}
                                                    disabled={
                                                        !(!selectedType || (selectedType && type === selectedType)) ||
                                                        (selectedProducts.size >= 4 && !isSelected({ id }))
                                                    }
                                                    catalogType={catalogType}
                                                    slugLabel={slugLabel}
                                                    newProduct={newProduct || false}
                                                    rootId={
                                                        type === 'corn' && id === tourListAnchorProductId
                                                            ? PRODUCT_EXPLORATION_TOUR_IDS.HYBRID_CARD
                                                            : type === 'crop_protection' &&
                                                                id === tourListAnchorProductId
                                                              ? PRODUCT_EXPLORATION_TOUR_IDS.CP_CARD
                                                              : undefined
                                                    }
                                                />
                                            </Grid2>
                                        );
                                    }
                                )
                            ) : (
                                <Grid2 xs={12}>
                                    <Drawing
                                        size='sm'
                                        type='library-not-found'
                                        title={_('No se encontraron resultados para tu búsqueda.')}
                                    />
                                </Grid2>
                            )
                        ) : (
                            [...Array(6)].map((_, i) => (
                                <Grid2 key={i} xs={12} sm={6} lg={4}>
                                    <Skeleton
                                        variant='rectangular'
                                        sx={{
                                            height: '400px',
                                            width: '100%',
                                        }}
                                    />
                                </Grid2>
                            ))
                        )}
                    </Grid2>
                </Grid2>
                <ProductComparator
                    selectedProducts={selectedProducts}
                    selectProduct={selectProduct}
                    selectedType={type}
                    resetSelectedProducts={resetSelectedProducts}
                    resetAndSelectMultipleProducts={resetAndSelectMultipleProducts}
                />
            </Stack>
        </PageContainer>
    );
};

export default ProductExplorationList;
